import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Phone, MapPin, Calendar, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { countries, popularCountries, timelineOptions } from '../data/consultationData';
import { apiService } from '../config/api';

const ConsultationModal = ({ isOpen, onClose, serviceType }) => {
  const [step, setStep] = useState('form'); // form, processing, success
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryOfResidence: '',
    targetCountry: '',
    timeline: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setStep('form');
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryOfResidence: '',
        targetCountry: '',
        timeline: ''
      });
      setErrors({});
      setResult(null);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.countryOfResidence) {
      newErrors.countryOfResidence = 'Country of residence is required';
    }

    if (!formData.targetCountry) {
      newErrors.targetCountry = 'Target country is required';
    }

    if (!formData.timeline) {
      newErrors.timeline = 'Timeline is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setStep('processing');

    try {
      const data = await apiService.submitConsultation({
        ...formData,
        serviceType
      });

      if (data.success) {
        setResult(data.data);
        setStep('success');
        
        // Open Calendly in new tab after a short delay
        setTimeout(() => {
          if (data.data.calendlyLink) {
            window.open(data.data.calendlyLink, '_blank');
          }
        }, 2000);
      } else {
        throw new Error(data.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStep('form');
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCountryOptions = (selectedValue) => {
    const allCountries = [...popularCountries, ...countries.filter(c => !popularCountries.find(pc => pc.code === c.code))];
    
    return allCountries.map(country => (
      <option key={country.code} value={country.name}>
        {country.name}
      </option>
    ));
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="consultation-modal-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            key="consultation-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {serviceType === 'study-abroad' ? 'Book a Free Consultation' : 'Book an Eligibility Consultation'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'form' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Please fill out this quick form to get your personalized consultation link.
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Phone (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number (Optional)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                {/* Country of Residence */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country of Residence *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      name="countryOfResidence"
                      value={formData.countryOfResidence}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 ${
                        errors.countryOfResidence ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select your country</option>
                      {getCountryOptions(formData.countryOfResidence)}
                    </select>
                  </div>
                  {errors.countryOfResidence && (
                    <p className="text-red-500 text-sm mt-1">{errors.countryOfResidence}</p>
                  )}
                </div>

                {/* Target Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Country *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      name="targetCountry"
                      value={formData.targetCountry}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 ${
                        errors.targetCountry ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select your target country</option>
                      {getCountryOptions(formData.targetCountry)}
                    </select>
                  </div>
                  {errors.targetCountry && (
                    <p className="text-red-500 text-sm mt-1">{errors.targetCountry}</p>
                  )}
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timeline *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 ${
                        errors.timeline ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select your timeline</option>
                      {timelineOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.timeline && (
                    <p className="text-red-500 text-sm mt-1">{errors.timeline}</p>
                  )}
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{errors.submit}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#202356] hover:bg-[#2d3270] text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>Get Consultation Link</span>
                  )}
                </button>
              </form>
            )}

            {step === 'processing' && (
              <div className="text-center py-8">
                <Loader className="animate-spin text-[#202356] mx-auto mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Finding Your Perfect Consultation
                </h3>
                <p className="text-gray-600">
                  We're matching you with the right consultant based on your needs...
                </p>
              </div>
            )}

            {step === 'success' && result && (
              <div className="text-center py-8">
                <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Perfect Match Found!
                </h3>
                <div className="space-y-3 mb-6">
                  <p className="text-gray-600">
                    <strong>Consultation Type:</strong> {result.consultationType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                  <p className="text-gray-600">
                    <strong>Estimated Wait Time:</strong> {result.estimatedWaitTime}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 text-sm">
                    <strong>Next Step:</strong> Your consultation booking page should open automatically in a new tab. If it doesn't, click the link below.
                  </p>
                </div>
                <button
                  onClick={() => window.open(result.calendlyLink, '_blank')}
                  className="w-full bg-[#202356] hover:bg-[#2d3270] text-white font-medium py-3 rounded-lg transition-colors mb-3"
                >
                  Open Booking Page
                </button>
                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsultationModal;
